<template>
  <v-card class="pa-4" outlined>
    <!-- Header -->
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col cols="12" md="6">
        <v-card-text class="pa-0 ma-0 text-body-1 text-right">
          {{ $t("productionReports") }}
        </v-card-text>
      </v-col>
      <v-col cols="12" md="6" class="text-left">
        <v-btn disabled @click="advancedFilterDrawer = true" small outlined>
          {{ $t("advancedFilters") }}
          <v-icon right>mdi-filter</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <!-- filters -->
    <v-row class="pa-0 ma-0 filters-row" no-gutters>
      <v-col
        cols="12"
        md="2"
        class="pa-1 ma-0"
        :class="['pa-0 ma-0', isTablet || isMobile ? 'mt-4' : '']"
      >
        <v-text-field
          v-model="filters.search"
          dense
          outlined
          :label="$t('search')"
          :placeholder="$t('search')"
          clearable
        >
          <template v-slot:prepend-inner>
            <v-icon>mdi-magnify</v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col
        cols="12"
        md="2"
        class="pa-1 ma-0"
        :class="['pa-0 ma-0', isTablet || isMobile ? 'mt-4' : '']"
      >
        <template>
          <v-text-field
            id="start"
            v-model="filters.fromDate"
            :label="$t('date')"
            clear-icon="flaticon2-close-cross"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-calendar-month"
          />
          <date-picker
            v-model="filters.fromDate"
            element="start"
            :locale="$store.state.date_locale"
            :format="$store.state.date_format"
            auto-submit
            :min="$store.state.startDate"
            :max="$store.state.endDate"
            :color="
              this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light']
                .primary
            "
            @close="setDate"
          />
        </template>
      </v-col>
      <v-col
        cols="12"
        md="2"
        class="pa-1 ma-0"
        :class="['pa-0 ma-0', isTablet || isMobile ? 'mt-4' : '']"
      >
        <template>
          <v-text-field
            id="end"
            v-model="filters.toDate"
            :label="$t('date')"
            clear-icon="flaticon2-close-cross"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-calendar-month"
          />
          <date-picker
            v-model="filters.toDate"
            element="end"
            :locale="$store.state.date_locale"
            :format="$store.state.date_format"
            auto-submit
            :min="$store.state.startDate"
            :max="$store.state.endDate"
            :color="
              this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light']
                .primary
            "
            @close="setDate"
          />
        </template>
      </v-col>
      <v-col
        cols="12"
        md="3"
        class="pa-1 ma-0"
        :class="['pa-0 ma-0', isTablet || isMobile ? 'mt-4' : '']"
      >
        <v-select
          v-model="filters.category"
          :items="categoryList"
          dense
          outlined
          :label="$t('category')"
        />
      </v-col>
      <v-col
        cols="12"
        md="3"
        class="pa-1 ma-0"
        :class="['pa-0 ma-0', isTablet || isMobile ? 'mt-4' : '']"
      >
        <v-select
          v-model="filters.status"
          :items="statusList"
          dense
          outlined
          :label="$t('status')"
        />
      </v-col>
    </v-row>

    <!-- Active Filters -->
    <v-row align="center" class="mt-4">
      <v-col cols="12" class="d-flex align-center flex-wrap">
        <v-card-text class="pa-0 ma-0 text-body-1 grey--text text--darken-1 ml-2">
          {{ $t("activeFilters") }}
        </v-card-text>

        <v-chip
          v-if="filters.status"
          close
          small
          class="ma-1 pa-4"
          color="#E8F2FF"
          text-color="#2F6DE1"
          @click:close="filters.status = null"
        >
          {{ $t("status") }}
          {{ filters.status }}
        </v-chip>

        <v-chip
          v-if="filters.category"
          close
          small
          class="ma-1 pa-4"
          color="#E8F2FF"
          text-color="#2F6DE1"
          @click:close="filters.category = null"
        >
          {{ $t("category") }}
          {{ filters.category }}
        </v-chip>

        <v-chip
          v-if="filters.search"
          close
          small
          class="ma-1 pa-4"
          color="#E8F2FF"
          text-color="#2F6DE1"
          @click:close="filters.search = null"
        >
          {{ $t("search") }}
          {{ filters.search }}
        </v-chip>

        <v-chip
          v-if="filters.fromDate"
          close
          small
          class="ma-1 pa-4"
          color="#E8F2FF"
          text-color="#2F6DE1"
          @click:close="filters.fromDate = null"
        >
          {{ $t("fromDate") }}
          {{ filters.fromDate }}
        </v-chip>

        <v-chip
          v-if="filters.toDate"
          close
          small
          class="ma-1 pa-4"
          color="#E8F2FF"
          text-color="#2F6DE1"
          @click:close="filters.toDate = null"
        >
          {{ $t("toDate") }}
          {{ filters.toDate }}
        </v-chip>

        <v-btn text small color="red" class="pa-0 ma-0 ml-2" @click="resetFilters">
          {{ $t("clearAll") }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- advance filter -->
    <v-navigation-drawer
      v-model="advancedFilterDrawer"
      fixed
      left
      temporary
      width="360"
      class="pa-0 py-2 ma-0"
    >
      <div class="d-flex justify-space-between flex-column" style="height: 100%">
        <!-- top section -->
        <v-container class="pa-0 ma-0 flex-grow-1" style="height: 100%">
          <!-- Header -->
          <v-container class="pa-0 ma-0">
            <v-container class="pa-0 ma-0 d-flex align-center justify-space-between">
              <v-container class="pa-0 pr-2 py-1 ma-0 d-flex flex-column gap-1">
                <v-card-text class="pa-0 ma-0 text-body-2">
                  {{ $t("advancedFilters") }}
                </v-card-text>
                <v-card-text class="pa-0 ma-0 grey--text text-body-2">
                  {{ $t("selectDesiredFilters") }}
                </v-card-text>
              </v-container>
              <v-btn icon @click="advancedFilterDrawer = false">
                <v-icon color="grey"> mdi-close </v-icon>
              </v-btn>
            </v-container>
          </v-container>
          <v-divider />
          <!-- Filters -->
          <v-container class="pa-0 px-3 py-2 ma-0 d-flex flex-column gap-3">
            <v-container class="pa-0 ma-0 d-flex flex-column gap-6">
              <v-text-field
                v-model="ProductName"
                dense
                outlined
                :label="$t('enterProductName')"
                clearable
                class="ma-0 pa-0"
              />
              <v-select
                dense
                outlined
                :label="$t('selectProductionLine')"
                clearable
                class="ma-0 pa-0"
              />
              <v-select
                dense
                outlined
                :label="$t('selectShift')"
                clearable
                class="ma-0 pa-0"
              />
            </v-container>
            <!-- Amount Range -->
            <v-card-text class="pa-0 ma-0 py-2 text-body-2">
              {{ $t("countRange") }}
            </v-card-text>
            <v-container class="pa-0 ma-0 d-flex flex-column gap-1">
              <v-range-slider
                v-model="filters.amountRange"
                :min="0"
                :max="1000"
                height="14"
                step="1"
                color="black"
                track-color="grey lighten-2"
                class="ma-0 pa-0"
              />
              <v-container class="d-flex justify-space-between pa-0 ma-0">
                <v-card-text class="pa-0 ma-0 text-body-2">{{
                  filters.amountRange[0]
                }}</v-card-text>
                <v-card-text class="pa-0 ma-0 text-body-2">{{
                  filters.amountRange[1]
                }}</v-card-text>
              </v-container>
            </v-container>

            <v-container class="pa-0 ma-0 d-flex flex-column gap-6">
              <v-select
                dense
                outlined
                :label="$t('selectStatus')"
                clearable
                class="ma-0 pa-0"
              />
            </v-container>

            <!-- Switches -->
            <v-container
              class="pa-0 ma-0 my-4 d-flex justify-space-between align-center gap-2"
            >
              <v-switch v-model="filters.showNonZero" inset class="mb-1 ma-0 pa-0" />
              <v-card-text class="pa-0 ma-0 text-body-1 text-right">
                {{ $t("showInactiveItems") }}
              </v-card-text>
            </v-container>
            <v-container
              class="pa-0 ma-0 d-flex justify-space-between align-center gap-2"
            >
              <v-switch class="pa-0 ma-0" v-model="filters.showDeleted" inset />
              <v-card-text class="pa-0 ma-0 text-body-1 text-right">
                {{ $t("showDeletedItems") }}
              </v-card-text>
            </v-container>
          </v-container>
        </v-container>
        <!-- Footer Buttons -->
        <v-container class="pa-0 px-3 ma-0 d-flex gap-1">
          <v-btn
            class="flex-grow-1 ml-1"
            small
            height="44"
            outlined
            color="grey darken-1"
            @click="resetAdvancedFilters"
          >
            {{ $t("clearAll") }}
          </v-btn>
          <v-btn
            class="white--text flex-grow-1 mr-1"
            small
            height="44"
            style="background: linear-gradient(90deg, #3f5efb, #8f49f3)"
          >
            {{ $t("apply filters") }}
          </v-btn>
        </v-container>
      </div>
    </v-navigation-drawer>
  </v-card>
</template>

<script>
import mixin from "@/Mixins/mixin";

export default {
  name: "FilterProduction",
  mixins: [mixin],
  data() {
    return {
      advancedFilterDrawer: false,
      fromMenu: false,
      toMenu: false,
      filters: {
        status: null,
        category: null,
        search: null,
        fromDate: null,
        toDate: null,
        amountRange: [0, 1000],
        showNonZero: false,
        showDeleted: false,
      },
      statusList: ["در انتظار", "پرداخت شده", "لغو شده"],
      categoryList: ["خرید", "فروش", "مرجوعی"],
    };
  },

  methods: {
    resetFilters() {
      this.filters = {
        status: null,
        category: null,
        search: null,
        fromDate: null,
        toDate: null,
        amountRange: [0, 100],
        showNonZero: false,
        showDeleted: false,
      };
    },
    resetAdvancedFilters() {
      this.filters = {
        status: null,
        category: null,
        search: null,
        fromDate: null,
        toDate: null,
        amountRange: [0, 100],
        showNonZero: false,
        showDeleted: false,
      };
    },
  },
};
</script>
<style scoped>
::v-deep .v-messages.theme--light {
  display: none !important;
}
::v-deep .v-label .v-label--active {
  right: -28px !important;
}
.filters-row .v-input {
  max-width: 100%;
}
</style>
